import type { TFunction } from 'i18next';

import type { EGoogleAdsKeywordMatchType } from '@feathr/blackbox';
import type { TAlertType } from '@feathr/components';
import { objectId } from '@feathr/hooks';

import type { IAdGroupKeyword } from '../AdStep.types';
import { isKeywordTooGeneric } from '../AdStep.utils';
import { getMatchTypeLabel } from './KeywordFields.utils';

/** Either separator works, so a column pasted from a spreadsheet and a typed row both parse. */
const ENTRY_SEPARATOR = /[,\n]/;

/** What one bulk add did, split by the outcome the user needs to act on. */
export interface IBulkAddResult {
  /** New rows, ready to append to the ad group's keywords. */
  added: IAdGroupKeyword[];
  /** Entries already on the ad group at this match type, or repeated within the paste. */
  skipped: string[];
  /** Entries Ad Grant policy blocks, so they were never added. */
  rejected: string[];
  /**
   * The entries still needing the user's attention, in the order they were given: policy
   * rejections, and duplicates of keywords already on the ad group. This is what the field keeps,
   * so anything that landed cannot be sitting there to be submitted a second time — at another
   * match type it would be added all over again.
   *
   * A duplicate of a keyword this same add landed is deliberately absent: it asks nothing of the
   * user. It stays counted in `skipped`, so the report still describes the list it was given.
   */
  unresolved: string[];
}

/** One `Alert`'s worth of outcome, ready to render. */
export interface IBulkAddSummary {
  type: TAlertType;
  title: string;
  /** The keywords this summary covers, as the user entered them. */
  keywords: string;
}

/**
 * The keywords in a pasted or typed list, in the order given.
 *
 * @param raw - The contents of the bulk add field.
 */
export function parseKeywordEntries(raw: string): string[] {
  return raw
    .split(ENTRY_SEPARATOR)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/**
 * The identity two keywords are compared on. Matches what the `uniqueBy` constraint on
 * `GoogleAdsAdGroup.keywords` does — it trims each part and lowercases by default — so bulk
 * add skips exactly the rows the model would flag.
 */
function toIdentity(text: string): string {
  return text.trim().toLowerCase();
}

/**
 * Sorts a bulk add's entries into the rows to append, the duplicates to skip, and the
 * entries Ad Grant policy rejects.
 *
 * Policy is checked before duplication so a single-word entry is always reported as the
 * policy problem it is, rather than as a duplicate of an earlier single word.
 *
 * @param raw - The contents of the bulk add field.
 * @param matchType - The match type every added keyword takes.
 * @param existing - The ad group's current positive keywords.
 */
export function getBulkAddResult(
  raw: string,
  matchType: EGoogleAdsKeywordMatchType,
  existing: readonly IAdGroupKeyword[],
): IBulkAddResult {
  /* Only the same match type collides — the model's identity includes it. */
  const taken = new Set(
    existing
      .filter((keyword) => keyword.matchType === matchType)
      .map((keyword) => toIdentity(keyword.text)),
  );
  const addedIdentities = new Set<string>();
  /* Guards `unresolved` against the same identity landing twice — e.g. an existing keyword, or
   * a policy-rejected one, pasted more than once in a single run. Each still counts in `skipped`
   * / `rejected` so the report describes the full list it was given. */
  const unresolvedIdentities = new Set<string>();
  const result: IBulkAddResult = { added: [], rejected: [], skipped: [], unresolved: [] };

  parseKeywordEntries(raw).forEach((text) => {
    if (isKeywordTooGeneric(text)) {
      result.rejected.push(text);
      const identity = toIdentity(text);
      if (!unresolvedIdentities.has(identity)) {
        unresolvedIdentities.add(identity);
        result.unresolved.push(text);
      }
      return;
    }

    const identity = toIdentity(text);
    if (taken.has(identity)) {
      result.skipped.push(text);
      /* Its twin landed in this same run, so there is nothing here for the user to fix. */
      if (!addedIdentities.has(identity) && !unresolvedIdentities.has(identity)) {
        unresolvedIdentities.add(identity);
        result.unresolved.push(text);
      }
      return;
    }

    taken.add(identity);
    addedIdentities.add(identity);
    result.added.push({ key: objectId(), matchType, text });
  });

  return result;
}

/**
 * The outcome of a bulk add as alerts, one per outcome that happened. An add can partly
 * succeed, so a single confirmation cannot say enough — a user who pasted a long list needs
 * to know which entries did not land and why.
 *
 * @param result - {@link IBulkAddResult} from {@link getBulkAddResult}.
 * @param t - Translation function.
 */
export function getBulkAddSummaries(result: IBulkAddResult, t: TFunction): IBulkAddSummary[] {
  const summaries: IBulkAddSummary[] = [];

  if (result.added.length) {
    summaries.push({
      keywords: result.added.map((keyword) => keyword.text).join(', '),
      /* Read off the rows themselves, so the reported match type cannot disagree with them. */
      title: t('Added {{count}} keyword as {{matchType}}.', {
        count: result.added.length,
        matchType: getMatchTypeLabel(result.added[0].matchType, t),
      }),
      type: 'success',
    });
  }

  if (result.skipped.length) {
    summaries.push({
      keywords: result.skipped.join(', '),
      title: t('Skipped {{count}} duplicate.', { count: result.skipped.length }),
      type: 'info',
    });
  }

  if (result.rejected.length) {
    summaries.push({
      keywords: result.rejected.join(', '),
      title: t(
        'Rejected {{count}} single-word keyword. Ad Grant policy requires two words or more.',
        {
          count: result.rejected.length,
        },
      ),
      type: 'danger',
    });
  }

  return summaries;
}
